/**
 * Child-safety rules for logins, guardian profile access, and health notes.
 * A billing household is not one of these rules.
 */

import { COACH_ROLES, COMMITTEE_ROLES } from './clubRoles';
import { isStaffInductionRoles } from './inductionLogin';

export const SYNTHETIC_SWIMMER_EMAIL_SUFFIX = '@local.marshal.invalid';

export const MINOR_LOGIN_ERROR = 'minor_login_blocked';

export const MINOR_LOGIN_MESSAGE =
  'This sign-in is for adults. A swimmer under 18 uses a parent or guardian login.';

export const GUARDIAN_REQUIRED_MESSAGE =
  'A swimmer under 18 needs a parent or guardian on this request. The swimmer does not get a login.';

export const GUARDIAN_AUTHORITIES = ['guardian', 'older_swimmer', 'club_recorded'] as const;
export type GuardianAuthority = (typeof GUARDIAN_AUTHORITIES)[number];

export const HEALTH_NOTE_AUTHORITIES = ['guardian', 'older_swimmer'] as const;
export type HealthNoteAuthority = (typeof HEALTH_NOTE_AUTHORITIES)[number];

/** Privacy Act s 49 treats under-16 interests as a reason to refuse access. */
export const OLDER_SWIMMER_YEARS = 16;

export const HEALTH_NOTE_READER_LIMIT = 8;

export const HEALTH_NOTE_BODY_LIMIT = 2000;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

function isoDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = ISO_DATE.exec(value.trim());
  if (!match) return null;
  return `${match[1]}-${match[2]}-${match[3]}`;
}

function calendarIso(day: Date): string {
  const y = day.getFullYear();
  const m = String(day.getMonth() + 1).padStart(2, '0');
  const d = String(day.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addYears(iso: string, years: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const shifted = new Date(y + years, (m ?? 1) - 1, d ?? 1);
  return calendarIso(shifted);
}

/**
 * Matches `users.is_minor`: date of birth is set and is after (today − 18 years).
 * On the 18th birthday the person is not a minor.
 */
export function isUnder18(dateOfBirth: string | null | undefined, today = new Date()): boolean {
  const dob = isoDate(dateOfBirth);
  if (!dob) return false;
  const cutoff = addYears(calendarIso(today), -18);
  return dob > cutoff;
}

/** Date of birth is present and the person is 18 or older. */
export function isKnownAdult(dateOfBirth: string | null | undefined, today = new Date()): boolean {
  return Boolean(isoDate(dateOfBirth)) && !isUnder18(dateOfBirth, today);
}

/**
 * Under-18 swimmers, and swimmers who are not a known adult, do not need a mobile.
 * Parents, coaches, and committee keep the existing requirement.
 */
export function memberPhoneOptional(opts: {
  dateOfBirth?: string | null;
  isSwimmer?: boolean;
  roles?: readonly string[];
}, today = new Date()): boolean {
  if (opts.roles && isStaffInductionRoles(opts.roles)) return false;
  if (opts.isSwimmer && !isKnownAdult(opts.dateOfBirth, today)) return true;
  return isUnder18(opts.dateOfBirth, today);
}

/** 16 or older — old enough to authorise a health and safety note. */
export function isOlderSwimmer(dateOfBirth: string | null | undefined, today = new Date()): boolean {
  const dob = isoDate(dateOfBirth);
  if (!dob) return false;
  const cutoff = addYears(calendarIso(today), -OLDER_SWIMMER_YEARS);
  return dob <= cutoff;
}

export function isSwimmerPlaceholderEmail(email: string | null | undefined): boolean {
  return Boolean(email?.trim().toLowerCase().endsWith(SYNTHETIC_SWIMMER_EMAIL_SUFFIX));
}

/** A real inbox. Blank and @local.marshal.invalid addresses are not logins. */
export function isDeliverableMemberEmail(email: string | null | undefined): boolean {
  const lower = email?.trim().toLowerCase() ?? '';
  if (!lower.includes('@')) return false;
  return !isSwimmerPlaceholderEmail(lower);
}

/**
 * Block a sign-in when the profile is flagged minor or the date of birth is under 18.
 * A missing date of birth is not enough on its own — adults are sometimes saved without one.
 * Callers that create swimmers still treat unknown age as guardian-required.
 * Prefer `signInBlockedAsMinor` at the login door so staff and non-swimmers are not caught
 * by a wrong date of birth.
 */
export function loginBlockedAsMinor(
  profile: { isMinor?: boolean | null; dateOfBirth?: string | null | undefined },
  today = new Date(),
): boolean {
  if (profile.isMinor) return true;
  return isUnder18(profile.dateOfBirth, today);
}

const SIGN_IN_EXEMPT_ROLES = new Set<string>([
  'platform_owner',
  ...COMMITTEE_ROLES,
  ...COACH_ROLES,
]);

/**
 * Committee, coach (including junior coach), and platform owner.
 * Used where staff keep their own login. Parent is a separate adult login role.
 */
export function hasSignInExemptRole(roles: readonly string[] | null | undefined): boolean {
  return (roles ?? []).some((role) => SIGN_IN_EXEMPT_ROLES.has(role));
}

const ADULT_LOGIN_ROLES = new Set<string>([
  'platform_owner',
  'parent',
  ...COMMITTEE_ROLES,
  ...COACH_ROLES,
]);

/**
 * Sign-in is allowed by role: parent, staff (committee or any coach, including
 * junior coach), or platform owner. An adult swimmer is allowed by age.
 * Roles stack — parent and secretary on one login still count.
 */
export function hasAdultLoginRole(roles: readonly string[] | null | undefined): boolean {
  return (roles ?? []).some((role) => ADULT_LOGIN_ROLES.has(role));
}

export type SuspiciousDobReason = 'january_first' | 'implausible_year';

/**
 * Placeholder and unlikely dates of birth. 1 January is the usual made-up
 * birthday. A year more than 100 ago, or in the future, is not a usable age.
 * A blank date is not suspicious — it was left empty on purpose.
 */
export function suspiciousDobReason(
  dateOfBirth: string | null | undefined,
  today = new Date(),
): SuspiciousDobReason | null {
  const dob = isoDate(dateOfBirth);
  if (!dob) return null;
  const [year, month, day] = dob.split('-').map(Number);
  const todayIso = calendarIso(today);
  const oldest = addYears(todayIso, -100);
  if (!year || !month || !day || dob < oldest || dob > todayIso) return 'implausible_year';
  if (month === 1 && day === 1) return 'january_first';
  return null;
}

export function suspiciousDobLabel(reason: SuspiciousDobReason): string {
  switch (reason) {
    case 'january_first':
      return '1 January — often a placeholder';
    case 'implausible_year':
      return 'Date of birth looks unlikely';
  }
}

/**
 * Who the sign-in screen and the API should turn away.
 * Parent, committee, coach (including junior coach), and platform owner are
 * never blocked — the date of birth is not the gate.
 * A known adult swimmer is not blocked.
 * Someone who is not a swimmer is not blocked.
 * Manages own account is an admin setting, off unless passed as true. It lets
 * an under-18 swimmer sign in with no coach or parent role.
 * An under-18 swimmer-only login is blocked. Omit `isSwimmer` to keep that strict.
 * A missing date of birth does not block.
 */
export function signInBlockedAsMinor(
  profile: {
    isMinor?: boolean | null;
    dateOfBirth?: string | null | undefined;
    roles?: readonly string[] | null;
    isSwimmer?: boolean | null;
    managesOwnAccount?: boolean | null;
  },
  today = new Date(),
): boolean {
  if (hasAdultLoginRole(profile.roles)) return false;
  if (profile.managesOwnAccount === true) return false;
  if (isKnownAdult(profile.dateOfBirth, today)) return false;
  if (profile.isSwimmer === false) return false;
  if (profile.isMinor || isUnder18(profile.dateOfBirth, today)) return true;
  return false;
}

/**
 * A swimmer who is not a known adult still has a real email, so welcome mail and
 * the parent app follow the child. Staff who also swim keep their own login.
 */
export function swimmerLoginShouldMoveToGuardian(
  opts: {
    email: string | null | undefined;
    dateOfBirth: string | null | undefined;
    isSwimmer: boolean;
    isStaff?: boolean;
    managesOwnAccount?: boolean | null;
  },
  today = new Date(),
): boolean {
  if (!opts.isSwimmer || opts.isStaff) return false;
  if (opts.managesOwnAccount === true) return false;
  if (!isDeliverableMemberEmail(opts.email)) return false;
  if (isKnownAdult(opts.dateOfBirth, today)) return false;
  return true;
}

export type MisplacedSwimmerLoginReason = 'under_18' | 'no_date_of_birth';

export function misplacedSwimmerLoginReason(
  opts: {
    email: string | null | undefined;
    dateOfBirth: string | null | undefined;
    isSwimmer: boolean;
    isStaff?: boolean;
    managesOwnAccount?: boolean | null;
  },
  today = new Date(),
): MisplacedSwimmerLoginReason | null {
  if (!swimmerLoginShouldMoveToGuardian(opts, today)) return null;
  return isUnder18(opts.dateOfBirth, today) ? 'under_18' : 'no_date_of_birth';
}

/**
 * A swimmer no longer counts as holding a login once the swimmer profile or
 * the club membership is archived. `swimmers` has no archived_at column;
 * profile archive is `swimmers.status = 'archived'` (and a set user
 * archived_at). Membership archive is `club_memberships.archived_at` or
 * induction status archived. Either one is enough.
 */
export function isArchivedSwimmerLogin(opts: {
  swimmerStatus?: string | null;
  userArchivedAt?: string | null;
  membershipArchivedAt?: string | null;
  inductionStatus?: string | null;
}): boolean {
  if (opts.userArchivedAt) return true;
  if (opts.membershipArchivedAt) return true;
  if (opts.inductionStatus === 'archived') return true;
  if (opts.swimmerStatus === 'archived') return true;
  return false;
}

export function selectSwimmersHoldingLogin<T extends {
  email: string | null | undefined;
  dateOfBirth: string | null | undefined;
  isSwimmer: boolean;
  isStaff?: boolean;
  managesOwnAccount?: boolean | null;
  swimmerStatus?: string | null;
  userArchivedAt?: string | null;
  membershipArchivedAt?: string | null;
  inductionStatus?: string | null;
}>(
  rows: readonly T[],
  opts?: { includeArchived?: boolean; today?: Date },
): {
  rows: Array<T & { reason: MisplacedSwimmerLoginReason; archived: boolean }>;
  archivedCount: number;
} {
  const today = opts?.today ?? new Date();
  const includeArchived = opts?.includeArchived === true;
  const visible: Array<T & { reason: MisplacedSwimmerLoginReason; archived: boolean }> = [];
  let archivedCount = 0;
  for (const row of rows) {
    const reason = misplacedSwimmerLoginReason(row, today);
    if (!reason) continue;
    const archived = isArchivedSwimmerLogin(row);
    if (archived) archivedCount += 1;
    if (archived && !includeArchived) continue;
    visible.push({ ...row, reason, archived });
  }
  return { rows: visible, archivedCount };
}

export type SwimmerMailboxPlan = {
  /** Auth address must be a non-deliverable @local.marshal.invalid mailbox. */
  useSyntheticAuthEmail: boolean;
  /** This create must not email a parent welcome to the swimmer. */
  sendParentWelcomeToSwimmer: false;
  /** Missing age is treated as under 18 so a child cannot skip the guardian. */
  guardianRequired: boolean;
};

/**
 * A new swimmer must not be created on a brand-new real email and then sent
 * a parent welcome. Under 18 (or unknown age) never gets a deliverable login.
 * A known adult may keep their own email, still without an automatic parent welcome.
 */
export function planSwimmerMailbox(opts: {
  dateOfBirth: string | null | undefined;
}): SwimmerMailboxPlan {
  const adult = isKnownAdult(opts.dateOfBirth);
  return {
    useSyntheticAuthEmail: !adult,
    sendParentWelcomeToSwimmer: false,
    guardianRequired: !adult,
  };
}

export type ProfileWardLink = {
  swimmerId: string;
  relationship: string;
  profileAccess: boolean;
};

/**
 * App access follows an explicit guardian link. `profileAccess: false` hides
 * the child and keeps the person. Mail (`receives_comms`) is not an input.
 * Billing-account siblings are not links — callers must not pass them here.
 */
export function profileWardsFromLinks(links: readonly ProfileWardLink[]): Array<{
  swimmerId: string;
  relationship: string;
}> {
  const denied = new Set<string>();
  const allowed = new Map<string, string>();
  for (const link of links) {
    if (!link.swimmerId) continue;
    if (!link.profileAccess) {
      denied.add(link.swimmerId);
      allowed.delete(link.swimmerId);
      continue;
    }
    if (denied.has(link.swimmerId) || allowed.has(link.swimmerId)) continue;
    allowed.set(link.swimmerId, link.relationship || 'guardian');
  }
  return [...allowed.entries()].map(([swimmerId, relationship]) => ({ swimmerId, relationship }));
}

/** Same Xero contact / billing household never opens a child profile by itself. */
export function billingHouseholdGrantsProfileAccess(): false {
  return false;
}

export type HealthNoteValidation =
  | { ok: true; authority: HealthNoteAuthority; readerUserIds: string[] }
  | { ok: false; error: string; message: string };

export function validateHealthSafetyNote(opts: {
  body: string | null | undefined;
  authority: string | null | undefined;
  swimmerDateOfBirth: string | null | undefined;
  swimmerUserId: string;
  authorityUserId: string | null | undefined;
  /** Guardian user ids that already have a swimmer_guardians row. */
  guardianUserIds: readonly string[];
  readerUserIds: readonly string[];
}): HealthNoteValidation {
  const text = opts.body?.trim() ?? '';
  if (!text) {
    return { ok: false, error: 'invalid_body', message: 'Write the health and safety note.' };
  }
  if (text.length > HEALTH_NOTE_BODY_LIMIT) {
    return {
      ok: false,
      error: 'invalid_body',
      message: `Health and safety notes are limited to ${HEALTH_NOTE_BODY_LIMIT} characters.`,
    };
  }
  if (opts.authority !== 'guardian' && opts.authority !== 'older_swimmer') {
    return {
      ok: false,
      error: 'invalid_authority',
      message: 'Authority must be a guardian or an older swimmer.',
    };
  }
  const authorityUserId = opts.authorityUserId?.trim() ?? '';
  if (!authorityUserId) {
    return { ok: false, error: 'invalid_authority', message: 'Say who authorised this note.' };
  }
  if (opts.authority === 'guardian' && !opts.guardianUserIds.includes(authorityUserId)) {
    return {
      ok: false,
      error: 'invalid_authority',
      message: 'Guardian authority has to be an adult already linked to this swimmer.',
    };
  }
  if (opts.authority === 'older_swimmer') {
    if (authorityUserId !== opts.swimmerUserId) {
      return {
        ok: false,
        error: 'invalid_authority',
        message: 'Older-swimmer authority has to be the swimmer themselves.',
      };
    }
    if (!isOlderSwimmer(opts.swimmerDateOfBirth)) {
      return {
        ok: false,
        error: 'invalid_authority',
        message: `Only a swimmer aged ${OLDER_SWIMMER_YEARS} or over can authorise their own health and safety note.`,
      };
    }
  }

  const readers = [...new Set(opts.readerUserIds.map((id) => id.trim()).filter(Boolean))];
  if (!readers.includes(authorityUserId)) readers.unshift(authorityUserId);
  if (readers.length > HEALTH_NOTE_READER_LIMIT) {
    return {
      ok: false,
      error: 'reader_list_too_long',
      message: `A health and safety note can have at most ${HEALTH_NOTE_READER_LIMIT} readers.`,
    };
  }
  return { ok: true, authority: opts.authority, readerUserIds: readers };
}
